import { NavLink } from "react-router";
import {
  Bot,
  PanelLeftClose,
  PanelLeftOpen,
  Database,
  FlaskConical,
  Home,
  PenLine,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useProject } from "@/features/projects/hooks";
import { useAgents } from "@/features/agents/hooks";
import { ProjectSwitcher } from "@/features/projects/project-switcher";
import { useUserAdminAccess } from "@/features/users/hooks";
import { useSidebarStore } from "@/stores/sidebar-store";
import { UserMenu } from "./user-menu";
import { ThemeSelector } from "./theme-selector";

type NavItem = { to: string; label: string; icon: LucideIcon; end?: boolean };

const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/agents", label: "Agents", icon: Bot },
  { to: "/datasets", label: "Datasets", icon: Database },
  { to: "/experiments", label: "Experiments", icon: FlaskConical },
  { to: "/evaluators", label: "Evaluators", icon: ShieldCheck },
  { to: "/annotation", label: "Annotate", icon: PenLine },
];

// One entry; connections and run credentials are tabs on the page. Not `end`,
// so it stays active on every /settings route.
const SETTINGS: NavItem[] = [{ to: "/settings", label: "Settings", icon: Settings }];

// Workbench-wide, not this project's: only for workbench admins (`users:read`).
const WORKBENCH: NavItem[] = [{ to: "/users", label: "Users", icon: Users }];

const groupLabel = "px-2.5 pb-1 text-[11px] font-semibold tracking-[0.06em] text-faint uppercase";

const item = ({ isActive }: { isActive: boolean }) =>
  `flex h-[34px] items-center gap-2.5 rounded-[7px] px-2.5 text-sm font-medium ${
    isActive ? "bg-nav-active text-ink" : "text-body hover:bg-nav-hover hover:text-ink"
  }`;

/** Collapsed, each link is its icon alone; its name stays as the accessible name and tooltip. */
function NavLinks({ items, collapsed }: { items: NavItem[]; collapsed: boolean }) {
  return items.map(({ to, label, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end ?? false}
      className={(s) => `${item(s)} ${collapsed ? "justify-center px-0" : ""}`}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
    >
      <Icon size={16} strokeWidth={1.8} aria-hidden />
      {!collapsed && label}
    </NavLink>
  ));
}

const toggleClass =
  "flex size-7 shrink-0 items-center justify-center rounded-md text-faint transition-colors hover:bg-nav-hover hover:text-ink";

function CollapseButton({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <button type="button" aria-label={label} title={label} onClick={onToggle} className={toggleClass}>
      <Icon size={16} strokeWidth={1.8} aria-hidden />
    </button>
  );
}

function YourAgents() {
  const { data: agents } = useAgents();
  if (!agents?.length) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <div className={groupLabel}>Your agents</div>
      {agents.map((agent) => (
        <NavLink
          key={agent.id}
          to={`/agents/${agent.id}`}
          className="flex h-[30px] items-center gap-2 rounded-md px-2.5 text-[13px] text-body hover:bg-nav-hover hover:text-ink"
        >
          <span
            aria-hidden
            className={`size-1.5 shrink-0 rounded-full ${agent.stats.running ? "bg-link" : "bg-dim"}`}
          />
          <span className="truncate">{agent.name}</span>
        </NavLink>
      ))}
    </div>
  );
}

/** What a nav entry needs: Home and Agents show experiment work, the rest their own records. */
const needs = (to: string) =>
  to === "/" ? "experiments:read" : `${to === "/annotation" ? "annotations" : to.slice(1)}:read`;

export function Sidebar() {
  const { can } = useProject();
  const { canRead: seesUsers } = useUserAdminAccess();
  // An annotator holds only `annotations:*`, so they see Annotate (and Settings, to leave).
  const visible = NAV.filter((n) => can(needs(n.to)));
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  return (
    <aside
      className={`flex shrink-0 flex-col gap-[18px] overflow-x-hidden overflow-y-auto border-r border-line bg-sidebar py-4 transition-[width] duration-200 ${
        collapsed ? "w-[60px] px-2" : "w-[220px] px-3"
      }`}
    >
      {/* Inside a project the switcher is the header, like a workspace switcher. */}
      <div className={`flex items-center gap-1 ${collapsed ? "flex-col-reverse gap-3" : ""}`}>
        <div className="min-w-0 flex-1 self-stretch">
          <ProjectSwitcher compact={collapsed} />
        </div>
        <CollapseButton collapsed={collapsed} onToggle={toggle} />
      </div>
      <nav aria-label="Main" className="flex flex-col gap-0.5">
        <NavLinks items={visible} collapsed={collapsed} />
      </nav>
      {!collapsed && can("agents:read") && <YourAgents />}
      <div className="flex-1" />
      {seesUsers && (
        <nav aria-label="Workbench" className="flex flex-col gap-0.5">
          {!collapsed && <div className={groupLabel}>Workbench</div>}
          <NavLinks items={WORKBENCH} collapsed={collapsed} />
        </nav>
      )}
      <nav aria-label="Settings" className="flex flex-col gap-0.5">
        <NavLinks items={SETTINGS} collapsed={collapsed} />
      </nav>
      {!collapsed && <ThemeSelector />}
      <UserMenu compact={collapsed} />
    </aside>
  );
}
